import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import PageHeader from '@modules/project/component/page-header/PageHeader';
import Summary from '@modules/project/component/summary/Summary';
import DetailsMobile from '@modules/project/component/details/Details.mobile';
import ContactsMobile from '@modules/project/component/contacts/Contacts.mobile';
import Awards from '@modules/project/component/awards/Awards';
import Details from '@modules/project/component/details/Details';
import { useLoaderData } from '@remix-run/react';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import DetailBannerSection from '~/modules/page-builder/component/detail/detail-banner-section/DetailBannerSection';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    BANNER_WIDTH_FULL_MOBILE,
    FULL_COLUMN_WIDTH_FULL_SCREEN,
} from '~/modules/page-builder/config/column-constants';
import DownloadAction from '~/modules/page-builder/component/detail/detail-banner-section/actions/DownloadAction';
import { getBannerDimensions } from '~/utils/banner-utils';
import * as S from './ProjectPageHeader.style';
import { ProjectEnquiryForm } from '@modules/project/component/project-enquiry/ProjectEnquiryForm';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import { getEnquiryMessagePlaceholder } from '~/modules/enquiry/util/get-enquiry-message-placeholder';
import { ProjectHeroBannerContent } from './ProjectHeroBannerContent';

interface ProjectPageHeaderProps {
    canDownloadBannerImage: boolean;
    onOpenEnquiryModal: (changeUrl?: boolean) => void;
}

const HIDE_ON_PAGE_ENQUIRY_FORM = true;

const ProjectPageHeader = ({
    canDownloadBannerImage,
    onOpenEnquiryModal,
}: ProjectPageHeaderProps) => {
    const { primaryCTA } = useLoaderData<typeof loader>();
    const project = useProjectPreviewRouteData();

    const { desktop } = useAppDisplayModeContext();
    const { css } = useStyles();

    const bannerSize = getImagesSizes({
        desktop: FULL_COLUMN_WIDTH_FULL_SCREEN,
        mobile: BANNER_WIDTH_FULL_MOBILE,
    });

    const bannerDimensions = getBannerDimensions('project');

    const messageBodyPlaceholder = getEnquiryMessagePlaceholder(
        project.Professional.Title,
        'project'
    );

    const banner = (
        <DetailBannerSection
            {...project.Banner}
            title={project.Title}
            dimensions={bannerDimensions}
            size={bannerSize}
            isVideoBanner={project.Banner.isVideoBanner}
            videoBannerUrl={project.Banner.videoBannerUrl ?? ''}
            showBackButton={false}
            awards={project.Awards}
            showOverlay={desktop}
            customOverlayElement={
                desktop ? (
                    <ProjectHeroBannerContent
                        project={project}
                        openEnquiryModal={onOpenEnquiryModal}
                        primaryCTA={primaryCTA}
                    />
                ) : undefined
            }
            actions={[
                <DownloadAction
                    key="downloadAction"
                    canDownload={canDownloadBannerImage}
                    downloadLink={project.Banner.downloadLink ?? ''}
                    libraryImageID={project.Banner.libraryImage?.ID ?? 0}
                />,
            ]}
        />
    );

    return (
        <>
            {desktop ? (
                <>
                    {project.Banner && banner}
                    <div className={css(S.Section)}>
                        <div className={css(S.SectionContent)}>
                            {project.Content && <Summary data={project} />}
                            <Details data={project} />
                            {!HIDE_ON_PAGE_ENQUIRY_FORM && (
                                <ProjectEnquiryForm
                                    data={{
                                        Professional: {
                                            ...project.Professional,
                                            Website:
                                                project.Professional.Website,
                                            primaryCTA,
                                        },
                                        website: project.Website,
                                        siteTreeID: project.ID,
                                        messageBodyPlaceholder,
                                        primaryCTA,
                                        itemType: 'project',
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {project.Banner && banner}
                    <PageHeader data={project} />
                    {project.Content && <Summary data={project} />}
                    <DetailsMobile data={project} />
                    <ContactsMobile data={project} />
                    {project.Awards.length !== 0 && (
                        <Awards awards={project.Awards} />
                    )}
                </>
            )}
        </>
    );
};
export default ProjectPageHeader;
